import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import Select from "react-select";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import { AuthContext } from "../../../../context/AuthContext";
import { fetchWithAuth } from "../../../../utils/api";
import {
  swalError,
  swalSuccess,
  swalConfirm,
  swalWarning,
} from "../../../../utils/swalAlert";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import PhoneInput from "react-phone-input-2";
import { userCountry } from "../../../../utils/getUserCountry";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import AddStudentsModal from "./AddStudentsModal";
import BulkPromoteModal from "./BulkPromoteModal";
import BulkUpdateStudentsModal from "./BulkUpdateStudentsModal";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const ManageStudents = forwardRef((props, ref) => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [levelOptions, setLevelOptions] = useState([]);
  const [feedbackLanguageOptions, setFeedbackLanguageOptions] = useState([]);
  const [instituteCategory, setInstituteCategory] = useState(null);
  const isLanguageInst =
    instituteCategory === "LANGUAGE_LEARNING_INSTITUTE" ||
    user?.category === "LANGUAGE_LEARNING_INSTITUTE" ||
    user?.institute?.category === "LANGUAGE_LEARNING_INSTITUTE";
  const [autoGenerateStudentPassword, setAutoGenerateStudentPassword] = useState(false);
  const [students, setStudents] = useState([]);
  const [isAddStudentsModalOpen, setIsAddStudentsModalOpen] = useState(false);
  const [isBulkUpdateModalOpen, setIsBulkUpdateModalOpen] = useState(false);
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [placementMap, setPlacementMap] = useState({});
  const [isPlacementReviewModalOpen, setIsPlacementReviewModalOpen] = useState(false);
  const [selectedPlacementStudent, setSelectedPlacementStudent] = useState(null);
  const [placementDetails, setPlacementDetails] = useState(null);
  const [isLoadingPlacementDetails, setIsLoadingPlacementDetails] = useState(false);
  const [confirmLevelForm, setConfirmLevelForm] = useState({
    level: "",
    remarks: "",
    autoEnroll: true,
  });
  const [isConfirmingLevel, setIsConfirmingLevel] = useState(false);
  const [editStudent, setEditStudent] = useState({
    id: "",
    firstName: "",
    lastName: "",
    referenceId: "",
    email: "",
    phoneNumber: "",
    receiveWhatsAppMessages: false,
    receiveEmailMessages: false,
    locked: false,
    batch: null,
    level: null,
    isActive: true,
    inActiveReason: "",
    markedAsDropout: false,
    dropoutAcademicYear: new Date().getFullYear(),
    feedbackLanguage: null,
    password: "",
  });
  const [initialEditStudent, setInitialEditStudent] = useState({
    id: "",
    firstName: "",
    lastName: "",
    referenceId: "",
    email: "",
    phoneNumber: "",
    receiveWhatsAppMessages: false,
    receiveEmailMessages: false,
    locked: false,
    batch: null,
    level: null,
    isActive: true,
    inActiveReason: "",
    markedAsDropout: false,
    feedbackLanguage: null,
  });
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false); // Loading state for update
  const [filteredStudents, setFilteredStudents] = useState([]);
  const tableRef = useRef(null);

  // Edit modal password states
  const [showEditPassword, setShowEditPassword] = useState(false);

  // Bulk delete states
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Send Message States
  const [isChannelSelectionModalOpen, setIsChannelSelectionModalOpen] = useState(false);
  const [isComposeMessageModalOpen, setIsComposeMessageModalOpen] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [messageForm, setMessageForm] = useState({
    subject: "",
    htmlBody: "",
    pushBody: "",
    attachments: [],
  });

  // Academic year options for dropout
  const dropoutAcademicYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const opts = [];
    for (let y = currentYear - 3; y <= currentYear + 2; y++) {
      const endShort = String(y + 1).slice(-2);
      opts.push({
        value: y,
        label: `${y}-${endShort}`,
      });
    }
    return opts;
  }, []);

  // Fetch institute data when user changes
  useEffect(() => {
    const fetchInstitute = async (instituteId) => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${instituteId}`,
          {
            method: "GET",
            credentials: "include",
          },
        );
        if (!response) return;
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch institute");
        }
        const data = await response.json();
        setInstituteCategory(data.category || "K12_SCHOOL");
        setAutoGenerateStudentPassword(!!data.autoGenerateStudentPassword);

        // Check if institute has custom levels
        if (data.levels && data.levels.length > 0) {
          setLevelOptions(
            data.levels.map((level) => ({
              value: level,
              label: level,
            })),
          );
        } else {
          // Fallback: Fetch levels from LOV
          try {
            const lovResponse = await fetchWithAuth(
              `${serverUrl}/api/lov/get-all-by-type/level`,
              {
                method: "GET",
                credentials: "include",
              },
            );
            if (lovResponse && lovResponse.ok) {
              const lovData = await lovResponse.json();
              setLevelOptions(
                lovData
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((level) => ({
                    value: level.value,
                    label: level.value,
                  })),
              );
            }
          } catch (lovError) {
            console.error("Failed to fetch levels from LOV:", lovError);
          }
        }
      } catch (error) {
        console.error("Failed to fetch institute:", error);
        swalError("Failed to fetch institute data.");
      }
    };
    if (user?.instituteId) {
      fetchInstitute(user.instituteId);
    }
  }, [user?.instituteId]);

  // Fetch feedback language options from LOV
  useEffect(() => {
    const fetchFeedbackLanguages = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/lov/get-all-by-type/feedbackLanguage`,
          {
            method: "GET",
            credentials: "include",
          },
        );
        if (response && response.ok) {
          const data = await response.json();
          setFeedbackLanguageOptions(
            data
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((item) => ({
                value: item.value,
                label: item.value,
              })),
          );
        }
      } catch (error) {
        console.error("Failed to fetch feedback languages:", error);
      }
    };
    fetchFeedbackLanguages();
  }, []);

  // Memoize batch year options: current year first, then -5 years, then +5 years
  const batchYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Current year first
    years.push({ value: currentYear, label: String(currentYear) });
    // Then -5 to -1 years
    for (let i = 1; i <= 5; i++) {
      years.push({ value: currentYear - i, label: String(currentYear - i) });
    }
    // Then +1 to +5 years
    for (let i = 1; i <= 5; i++) {
      years.push({ value: currentYear + i, label: String(currentYear + i) });
    }
    return years;
  }, []);

  // Check if students have batch/section data for conditional column rendering
  const hasLevelData = useMemo(() => {
    return students.some(
      (stu) =>
        stu.level !== null && stu.level !== undefined && stu.level !== "",
    );
  }, [students]);

  const hasBatchData = useMemo(() => {
    return students.some(
      (stu) =>
        stu.batch !== null && stu.batch !== undefined && stu.batch !== "",
    );
  }, [students]);

  // Memoized callback for filtered data to prevent infinite loops
  const handleDataFiltered = useCallback((data) => {
    setFilteredStudents(data);
  }, []);

  // Export students to Excel (.xlsx)
  const exportToExcel = async () => {
    // Use filtered data if filters are applied, otherwise use all students
    const dataToExport =
      filteredStudents.length > 0 ? filteredStudents : students;

    if (dataToExport.length === 0) {
      swalError("No students to export.");
      return;
    }

    const headers = [
      "firstName",
      "lastName",
      "referenceId",
      "rollNo",
      "email",
      "phoneNumber",
      "emailMessages",
      "whatsAppMessages",
      "locked",
    ];

    try {
      // Create workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Students");

      // Add headers row with bold styling
      const headerRow = worksheet.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center" };
      });

      // Add data rows
      dataToExport.forEach((stu) => {
        const row = worksheet.addRow([
          stu.firstName || "",
          stu.lastName || "",
          stu.referenceId || "",
          stu.rollNo || "",
          stu.email || "",
          stu.phoneNumber || "",
          stu.receiveEmailMessages ? "Yes" : "No",
          stu.receiveWhatsAppMessages ? "Yes" : "No",
          stu.locked ? "Yes" : "No",
        ]);
        // Format phone number cell as text to prevent scientific notation
        const phoneCell = row.getCell(6); // Column F (phoneNumber)
        if (stu.phoneNumber) {
          phoneCell.value = String(stu.phoneNumber);
          phoneCell.numFmt = "@"; // Text format
        }
      });

      // Set column widths and formats
      worksheet.columns = [
        { width: 15 }, // firstName
        { width: 15 }, // lastName
        { width: 15 }, // referenceId
        { width: 12 }, // rollNo
        { width: 30 }, // email
        { width: 20 }, // phoneNumber
        { width: 15 }, // emailMessages
        { width: 18 }, // whatsAppMessages
        { width: 12 }, // locked
      ];

      // Generate filename with level/batch from data and date
      // Get unique levels or batches from the exported data
      let categoryLabel = "All";
      if (instituteCategory !== "HIGHER_EDUCATION") {
        const uniqueLevels = [
          ...new Set(dataToExport.map((s) => s.level).filter(Boolean)),
        ];
        categoryLabel =
          uniqueLevels.length === 1
            ? uniqueLevels[0]
            : uniqueLevels.length > 1
              ? "Multiple_Levels"
              : "All_Levels";
      } else if (instituteCategory === "HIGHER_EDUCATION") {
        const uniqueBatches = [
          ...new Set(dataToExport.map((s) => s.batch).filter(Boolean)),
        ];
        categoryLabel =
          uniqueBatches.length === 1
            ? String(uniqueBatches[0])
            : uniqueBatches.length > 1
              ? "Multiple_Batches"
              : "All_Batches";
      }

      // Format date as dd-mm-yyyy
      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`;
      const filename = `Students_(${categoryLabel})_${dateStr}.xlsx`;

      // Write and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, filename);
      swalSuccess("Exported successfully!");
    } catch (err) {
      swalError("Failed to export file.");
    }
  };

  // Handle row selection for Students table
  const handleRowSelect = (e, studentId) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setSelectedStudentIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  // Handle select all for Students table
  const handleSelectAll = (visibleItemIds, areAllVisibleSelected) => {
    setSelectedStudentIds((prev) => {
      const newSet = new Set(prev);
      if (areAllVisibleSelected) {
        // Deselect all visible
        visibleItemIds.forEach((id) => newSet.delete(id));
      } else {
        // Select all visible
        visibleItemIds.forEach((id) => newSet.add(id));
      }
      return newSet;
    });
  };

  // Handlers for Message Modals
  const handleSelectChannel = (channels) => {
    setSelectedChannels(channels);
    setIsChannelSelectionModalOpen(false);
    setIsComposeMessageModalOpen(true);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!selectedChannels || selectedChannels.length === 0) {
      swalError("No channel selected.");
      return;
    }

    const hasEmail = selectedChannels.includes("email");
    const hasPush = selectedChannels.includes("push");

    if (hasEmail && (!messageForm.subject.trim() || !messageForm.htmlBody.trim())) {
      swalError("Subject and Email Body are required for email messages.");
      return;
    }

    if (hasPush && !messageForm.pushBody.trim()) {
      swalError("Notification Body is required for push messages.");
      return;
    }

    setIsSendingMessage(true);

    try {
      const formData = new FormData();

      formData.append("targetType", "students");
      formData.append(
        "studentIds",
        JSON.stringify(Array.from(selectedStudentIds)) // selectedStudentIds is a set
      );
      formData.append("channels", JSON.stringify(selectedChannels));

      formData.append(
        "message",
        JSON.stringify({
          subject: messageForm.subject,
          htmlBody: `<p>${messageForm.htmlBody.replace(/\n/g, "<br/>")}</p>`,
          pushBody: messageForm.pushBody,
        })
      );

      if (hasEmail) {
        messageForm.attachments.forEach((file) => {
          formData.append("attachments", file);
        });
      }

      const response = await fetchWithAuth(`${serverUrl}/api/institute/send-message`, {
        method: "POST",
        credentials: "include",
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.failed > 0) {
          const errorRowsHtml = data.failedNotifications.map(f =>
            `<tr>
              <td style="padding: 8px; border: 1px solid #dee2e6;">${f.studentName}</td>
              <td style="padding: 8px; border: 1px solid #dee2e6; text-transform: capitalize;">${f.channel}</td>
              <td style="padding: 8px; border: 1px solid #dee2e6; color: #dc3545;">${f.reason}</td>
            </tr>`
          ).join("");

          const html = `
            <div style="margin-bottom: 15px; font-weight: bold;">
              <span style="color: #28a745;">✓ Successful: ${data.successful}</span> | 
              <span style="color: #dc3545;">✗ Failed: ${data.failed}</span>
            </div>
            <div style="max-height: 250px; overflow-y: auto;">
              <table style="width: 100%; border-collapse: collapse; font-size: 0.85em; text-align: left;">
                <thead style="background: #f8f9fa; position: sticky; top: 0;">
                  <tr>
                    <th style="padding: 8px; border: 1px solid #dee2e6;">Student</th>
                    <th style="padding: 8px; border: 1px solid #dee2e6;">Channel</th>
                    <th style="padding: 8px; border: 1px solid #dee2e6;">Reason</th>
                  </tr>
                </thead>
                <tbody>${errorRowsHtml}</tbody>
              </table>
            </div>
          `;
          Swal.fire({
            icon: 'warning',
            title: 'Message Sending Summary',
            html: html,
            width: '80%',
            confirmButtonText: 'Got it'
          });
        } else {
          swalSuccess(`Successfully sent messages to ${data.successful} students!`);
        }

        setIsComposeMessageModalOpen(false);
        setMessageForm({ subject: "", htmlBody: "", pushBody: "", attachments: [] });
        setSelectedStudentIds(new Set()); // Deselect all after success
      } else {
        swalError(data.message || "Failed to send messages.");
      }
    } catch (err) {
      console.error("Error sending message:", err);
      swalError("An error occurred while sending the message.");
    } finally {
      setIsSendingMessage(false);
    }
  };

  // Handle Bulk Delete Students
  const handleBulkDelete = async () => {
    if (selectedStudentIds.size === 0) return;

    const confirmResult = await swalConfirm(
      "Remove selected students?",
      `Are you sure you want to permanently remove ${selectedStudentIds.size} student(s)? This action cannot be undone.`,
      "Yes, Remove!",
      "No, Cancel",
    );

    if (!confirmResult.isConfirmed) return;

    setIsBulkDeleting(true);

    // Find student details for the summary
    const studentsToDelete = students.filter((s) =>
      selectedStudentIds.has(s._id),
    );
    const successRecords = [];
    const errorRecords = [];

    const promises = studentsToDelete.map(async (student) => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/user/delete/${student._id}`,
          {
            method: "DELETE",
            credentials: "include",
          },
        );
        if (response && response.ok) {
          return { student, success: true };
        }
        const errorData = await response.json().catch(() => ({}));
        return {
          student,
          success: false,
          error: errorData.message || "Failed to delete",
        };
      } catch (error) {
        console.error(`Failed to delete student ${student._id}`, error);
        return {
          student,
          success: false,
          error: error.message || "Network error",
        };
      }
    });

    try {
      const results = await Promise.all(promises);

      const successfulIds = new Set();
      results.forEach((res) => {
        if (res.success) {
          successfulIds.add(res.student._id);
          successRecords.push(res.student);
        } else {
          errorRecords.push({ ...res.student, error: res.error });
        }
      });

      // Update local state by filtering out deleted students
      setStudents((prev) => prev.filter((s) => !successfulIds.has(s._id)));

      // Clear selection
      setSelectedStudentIds(new Set());

      // Create summary HTML
      const successRowsHtml = successRecords
        .map(
          (s) =>
            `<tr style="background: #d4edda;">
          <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724;">✓</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.firstName} ${s.lastName}</td>
          <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724; font-weight: 600;">Deleted</td>
        </tr>`,
        )
        .join("");

      const errorRowsHtml = errorRecords
        .map(
          (s) =>
            `<tr style="background: #f8d7da;">
          <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">✗</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.firstName} ${s.lastName}</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">${s.error}</td>
        </tr>`,
        )
        .join("");

      const tableHtml = `
        <div style="max-height: 400px; overflow: auto; margin-top: 10px; -webkit-overflow-scrolling: touch;">
          <div style="min-width: 600px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 0.85em; white-space: nowrap;">
              <thead>
                <tr style="background: #f8f9fa; position: sticky; top: 0;">
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center; width: 40px;"></th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center;">Student Name</th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${successRowsHtml}
                ${errorRowsHtml}
              </tbody>
            </table>
          </div>
        </div>
        <p style="margin-top: 8px; font-size: 0.75em; color: #888; text-align: center;">Swipe horizontally to see all columns</p>
        <div style="margin-top: 12px; display: flex; gap: 20px; justify-content: center; flex-wrap: wrap;">
          <span style="color: #155724; font-weight: 600;">✓ Deleted: ${successRecords.length}</span>
          <span style="color: #721c24; font-weight: 600;">✗ Failed: ${errorRecords.length}</span>
        </div>
      `;

      const icon =
        errorRecords.length === 0
          ? "success"
          : successRecords.length === 0
            ? "error"
            : "info";
      const title =
        errorRecords.length === 0
          ? "All Selected Students Deleted"
          : successRecords.length === 0
            ? "Deletion Failed"
            : "Deletion Summary";

      Swal.fire({
        icon: icon,
        title: title,
        html: tableHtml,
        width: "90%",
        confirmButtonText: "Got it!",
      });
    } catch (err) {
      console.error("Bulk delete error:", err);
      swalError("An error occurred during bulk deletion.");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const columns = [
    { key: "firstName", label: "First Name", sortable: true },
    { key: "lastName", label: "Last Name", sortable: true },
    { key: "referenceId", label: "Reference ID", sortable: true },
    { key: "rollNo", label: "Roll No", sortable: true },
    hasLevelData && {
      key: "level",
      label: isLanguageInst ? "CEFR Level" : "Level",
      sortable: true,
      filter: true,
      sortBy: (row) => {
        const val = row.level;
        if (!val || val === "-") return 999999;
        const match = String(val).match(/\d+/);
        return match ? parseInt(match[0], 10) : val;
      },
    },
    hasBatchData && {
      key: "batch",
      label: "Batch",
      sortable: true,
      filter: true,
    },
    feedbackLanguageOptions.length > 0 && {
      key: "feedbackLanguage",
      label: "Feedback Language",
      sortable: true,
      filter: true,
    },
    { key: "email", label: "Email", sortable: true },
    { key: "phoneNumber", label: "Phone Number", sortable: true },
    { key: "isLocked", label: "Locked", sortable: true, filter: true },
    { key: "isWhatsApp", label: "WhatsApp", sortable: false },
    { key: "isEmail", label: "Email Message", sortable: false },
    !showActiveOnly && { key: "statusReason", label: "Remarks", sortable: true, filter: true },
    { key: "edit", label: "", sortable: false },
  ].filter(Boolean);

  // Expose openModal method to parent via ref
  useImperativeHandle(ref, () => ({
    openModal: () => setIsAddStudentsModalOpen(true),
  }));

  // helper to strip plus, dashes, spaces and parentheses
  const cleanPhone = (val) =>
    val ? String(val).replace(/[+\-\s()]/g, "") : "";

  // Fetch Students from API
  const fetchStudents = useCallback(async () => {
    if (!user?.instituteId) {
      setErrors({ fetch: "No institute ID found." });
      setLoading(false);
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/user/get-all/${user.instituteId}/student`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch students");
      const data = await response.json();

      const filteredUsers = data.filter(
        (stu) =>
          stu.role[0] === "student" && stu.instituteId === user.instituteId,
      );

      setStudents(filteredUsers);
      setErrors({});

      // If language institute, also fetch placement tests to track evaluation status
      if (isLanguageInst) {
        try {
          const ptRes = await fetchWithAuth(
            `${serverUrl}/api/level-test/students`,
            { credentials: "include" },
          );
          if (ptRes && ptRes.ok) {
            const ptData = await ptRes.json();
            const pMap = {};
            const candidateList = Array.isArray(ptData)
              ? ptData
              : Array.isArray(ptData?.data)
              ? ptData.data
              : [];
            candidateList.forEach((c) => {
              if (c._id && c.placementTest) {
                const pt = c.placementTest;
                const isApproved = pt.placementStatus === "Approved";
                pMap[c._id] = {
                  ...pt,
                  _id: pt.testId || pt._id,
                  testId: pt.testId || pt._id,
                  isReportReady:
                    !isApproved &&
                    pt.writtenScore != null &&
                    pt.oralStatus === "Completed",
                };
              }
            });
            setPlacementMap(pMap);
          }
        } catch (ptErr) {
          console.error("Failed to load placement test statuses:", ptErr);
        }
      }
    } catch (err) {
      setErrors({ fetch: err.message });
      swalError(err.message || "Failed to load students.");
    } finally {
      setLoading(false);
    }
  }, [user?.instituteId, isLanguageInst]);

  // Open placement test evaluation review modal for admin
  const handleOpenPlacementReview = async (student, ptData) => {
    setSelectedPlacementStudent(student);
    const candidatePt = ptData || placementMap[student._id];
    const initialTarget = candidatePt?.targetLevel || student.level || "";
    setConfirmLevelForm({
      level: initialTarget,
      remarks: "",
      autoEnroll: true,
    });
    setPlacementDetails(null);
    setIsPlacementReviewModalOpen(true);

    const testId = candidatePt?._id || candidatePt?.testId;
    if (testId) {
      setIsLoadingPlacementDetails(true);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/level-test/details/${testId}`,
          { credentials: "include" },
        );
        if (response && response.ok) {
          const data = await response.json();
          setPlacementDetails(data);
          const recLevel =
            data.scores?.oral?.recommendation ||
            data.test?.targetLevel ||
            initialTarget;
          if (recLevel) {
            setConfirmLevelForm((prev) => ({ ...prev, level: recLevel }));
          }
        }
      } catch (err) {
        console.error("Failed to load placement test details:", err);
      } finally {
        setIsLoadingPlacementDetails(false);
      }
    }
  };

  // Confirm student's final level and activate their account
  const handleConfirmPlacementLevel = async (e) => {
    e.preventDefault();
    if (!confirmLevelForm.level) {
      swalError("Please select a CEFR level to assign to the student.");
      return;
    }

    setIsConfirmingLevel(true);
    try {
      const candidatePt = placementMap[selectedPlacementStudent?._id];
      const testId = placementDetails?.test?._id || candidatePt?.testId || candidatePt?._id;

      const response = await fetchWithAuth(
        `${serverUrl}/api/level-test/confirm-level`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            studentId: selectedPlacementStudent._id,
            testId: testId || undefined,
            confirmedLevel: confirmLevelForm.level,
            remarks: confirmLevelForm.remarks,
            autoEnroll: confirmLevelForm.autoEnroll,
          }),
        },
      );

      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to confirm level");
      }

      swalSuccess(
        `Level ${confirmLevelForm.level} confirmed! Student account has been activated.`,
      );
      setIsPlacementReviewModalOpen(false);
      const confirmedStudentId = selectedPlacementStudent?._id;
      const assignedLevel = confirmLevelForm.level;
      setSelectedPlacementStudent(null);
      setPlacementDetails(null);

      if (confirmedStudentId) {
        setPlacementMap((prev) => {
          if (!prev[confirmedStudentId]) return prev;
          return {
            ...prev,
            [confirmedStudentId]: {
              ...prev[confirmedStudentId],
              placementStatus: "Approved",
              isReportReady: false,
            },
          };
        });
        setStudents((prev) =>
          prev.map((s) =>
            s._id === confirmedStudentId
              ? {
                  ...s,
                  level: assignedLevel,
                  isActive: true,
                  inActiveReason: null,
                }
              : s,
          ),
        );
      }
      fetchStudents();
    } catch (err) {
      console.error("Error confirming level:", err);
      swalError(err.message || "Failed to confirm level.");
    } finally {
      setIsConfirmingLevel(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchStudents();
    }
  }, [fetchStudents, isAuthenticated]);

  // Validate the edit form
  const validateEditForm = () => {
    const newErrors = {};
    if (!editStudent.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!editStudent.lastName.trim())
      newErrors.lastName = "Last name is required";
    if (!editStudent.email?.trim() && !editStudent.referenceId?.trim())
      newErrors.email = "A valid Reference ID or Email is required";
    if (editStudent.email && !/\S+@\S+\.\S+/.test(editStudent.email))
      newErrors.email = "Email is invalid";
    if (editStudent.password && editStudent.password.length < 8)
      newErrors.password = "Password must be at least 8 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if edit form has changed
  const checkEditChanged = useCallback(
    (updated) => {
      return (
        updated.firstName !== initialEditStudent.firstName ||
        updated.lastName !== initialEditStudent.lastName ||
        updated.referenceId !== initialEditStudent.referenceId ||
        updated.rollNo !== initialEditStudent.rollNo ||
        updated.email !== initialEditStudent.email ||
        cleanPhone(updated.phoneNumber) !==
        cleanPhone(initialEditStudent.phoneNumber) ||
        updated.locked !== initialEditStudent.locked ||
        updated.receiveWhatsAppMessages !==
        initialEditStudent.receiveWhatsAppMessages ||
        updated.receiveEmailMessages !==
        initialEditStudent.receiveEmailMessages ||
        updated.feedbackLanguage !==
        initialEditStudent.feedbackLanguage ||
        updated.markedAsDropout !== initialEditStudent.markedAsDropout ||
        updated.dropoutAcademicYear !== initialEditStudent.dropoutAcademicYear ||
        updated.password !== initialEditStudent.password
      );
    },
    [initialEditStudent]
  );

  // Handle input changes in the edit modal
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setEditStudent((prev) => {
      const updated = { ...prev, [name]: newValue };
      setIsEditChanged(checkEditChanged(updated));
      return updated;
    });
  };

  // Handle Edit Student
  const handleEditStudent = async (e) => {
    e.preventDefault();
    if (!validateEditForm()) return;

    setIsUpdating(true); // Start loading
    try {
      const requestBody = {
        firstName: editStudent.firstName,
        lastName: editStudent.lastName,
        referenceId: editStudent.referenceId,
        rollNo: editStudent.rollNo?.trim() || null,
        email: editStudent.email,
        // send cleaned phone number (digits only)
        phoneNumber: cleanPhone(editStudent.phoneNumber),
        receiveWhatsAppMessages: editStudent.receiveWhatsAppMessages,
        receiveEmailMessages: editStudent.receiveEmailMessages,
        locked: editStudent.locked,
        batch: editStudent.batch ? Number(editStudent.batch) : null,
        level: editStudent.level || null,
        feedbackLanguage: editStudent.feedbackLanguage || null,
      };

      if (editStudent.password) {
        requestBody.password = editStudent.password;
      }

      // Handle dropout toggle
      if (editStudent.markedAsDropout) {
        const endShort = String(editStudent.dropoutAcademicYear + 1).slice(-2);
        requestBody.level = "";
        requestBody.isActive = false;
        requestBody.inActiveReason = `Dropout_${editStudent.dropoutAcademicYear}-${endShort}`;
      } else {
        requestBody.level = editStudent.level || null;
        // If the student was previously a dropout and we're restoring them
        if (initialEditStudent.markedAsDropout && !editStudent.markedAsDropout) {
          requestBody.isActive = true;
          requestBody.inActiveReason = "";
        }
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/user/update/${editStudent.id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update student");
      }

      const updatedStudent = await response.json();
      setStudents(
        students.map((stu) =>
          stu._id === updatedStudent._id ? updatedStudent : stu,
        ),
      );
      setIsEditModalOpen(false);
      const emptyState = {
        id: "",
        firstName: "",
        lastName: "",
        referenceId: "",
        rollNo: "",
        email: "",
        phoneNumber: "",
        receiveWhatsAppMessages: false,
        receiveEmailMessages: false,
        locked: false,
        batch: null,
        level: null,
        isActive: true,
        inActiveReason: "",
        markedAsDropout: false,
        dropoutAcademicYear: new Date().getFullYear(),
        feedbackLanguage: null,
        password: "",
      };
      setInitialEditStudent(emptyState);
      setIsEditChanged(false);
      swalSuccess("Student updated successfully");
    } catch (err) {
      swalError(err.message || "Failed to update student.");
    } finally {
      setIsUpdating(false);
    }
  };

  // Filter students by active status (always include students with Level Test Required or active placement tests)
  const displayStudents = useMemo(() => {
    if (showActiveOnly) {
      return students.filter(
        (s) =>
          s.isActive !== false ||
          s.inActiveReason === "Level Test Required" ||
          !!placementMap[s._id],
      );
    }
    return students;
  }, [students, showActiveOnly, placementMap]);

  // Enhance Students Data with Action Buttons
  const enhancedStudents = displayStudents.map((stu) => {
    let rowStyle = {};
    let reasonDisplay = "-";

    const pt = placementMap[stu._id];
    const isLevelTestRequired =
      stu.inActiveReason === "Level Test Required" ||
      (stu.isActive === false && (!stu.level || stu.level === "-"));

    const isAlreadyAssigned =
      Boolean(stu.level && stu.level !== "-") &&
      stu.isActive !== false &&
      stu.inActiveReason !== "Level Test Required";

    const isReportReady =
      !isAlreadyAssigned &&
      pt?.placementStatus !== "Approved" &&
      Boolean(
        pt?.isReportReady ||
        (pt?.writtenScore != null && pt?.oralStatus === "Completed") ||
        pt?.status === "ready_decision"
      );

    if (stu.isActive === false) {
      const reasonLower = (stu.inActiveReason || "").toLowerCase();
      if (isLevelTestRequired || pt) {
        rowStyle = { backgroundColor: "rgba(240, 154, 33, 0.08)" }; // Light amber for level test required
      } else if (reasonLower.includes("dropout")) {
        rowStyle = { backgroundColor: "rgba(239, 68, 68, 0.1)" }; // Light Red
      } else if (reasonLower.includes("passout")) {
        rowStyle = { backgroundColor: "rgba(240, 154, 33, 0.1)" }; // Light orange
      } else {
        rowStyle = { backgroundColor: "rgba(156, 163, 175, 0.1)" }; // Light Gray for other inactive
      }

      reasonDisplay = stu.inActiveReason ? stu.inActiveReason.replace(/_/g, " ") : "Inactive";
    } else if (!showActiveOnly) {
      reasonDisplay = "Active";
    }

    let levelDisplay = stu.level || "-";
    if (isLanguageInst) {
      if (isAlreadyAssigned || (stu.level && stu.level !== "-")) {
        levelDisplay = (
          <span
            className={`cefr-badge cefr-${String(stu.level)
              .toLowerCase()
              .replace(/\./g, "-")}`}
          >
            {stu.level}
          </span>
        );
      } else if (isReportReady) {
        const totalScore = (pt?.writtenScore || 0) + (pt?.oralScore || 0);
        const totalMax = (pt?.writtenMaxScore || 70) + (pt?.oralMaxScore || 30);
        levelDisplay = (
          <span
            style={{
              backgroundColor: "#dcfce7",
              color: "#15803d",
              border: "1.5px solid #86efac",
              padding: "3px 8px",
              borderRadius: "12px",
              fontSize: "0.75rem",
              fontWeight: "700",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
              boxShadow: "0 1px 2px rgba(22, 163, 74, 0.15)",
            }}
            onClick={() => handleOpenPlacementReview(stu, pt)}
            title={`Teacher evaluation complete! Score: ${totalScore}/${totalMax}. Click to review scorecard & assign official CEFR level.`}
          >
            <i className="fa-solid fa-circle-check" style={{ color: "#16a34a" }}></i>
            Report Ready ({totalScore} pts)
          </span>
        );
      } else if (pt?.writtenScore != null) {
        levelDisplay = (
          <span
            style={{
              backgroundColor: "#fef3c7",
              color: "#92400e",
              border: "1px solid #fde68a",
              padding: "3px 8px",
              borderRadius: "12px",
              fontSize: "0.75rem",
              fontWeight: "600",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
            title="Written test graded, awaiting teacher oral viva"
          >
            Oral Viva Pending
          </span>
        );
      } else if (pt) {
        levelDisplay = (
          <span
            style={{
              backgroundColor: "#e0f2fe",
              color: "#0369a1",
              border: "1px solid #bae6fd",
              padding: "3px 8px",
              borderRadius: "12px",
              fontSize: "0.75rem",
              fontWeight: "600",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
            title="Placement test allotted to student, awaiting exam"
          >
            Test In Progress
          </span>
        );
      } else if (isLevelTestRequired) {
        levelDisplay = (
          <span
            style={{
              backgroundColor: "#fff7ed",
              color: "#c2410c",
              border: "1px solid #ffedd5",
              padding: "3px 8px",
              borderRadius: "12px",
              fontSize: "0.75rem",
              fontWeight: "600",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
            title="Student enrolled for level test, awaiting teacher test creation"
          >
            Level Test Required
          </span>
        );
      }
    }

    return {
      ...stu,
      _rowStyle: rowStyle,
      rollNo: stu.rollNo || "-",
      level: levelDisplay,
      batch: stu.batch || "-",
      feedbackLanguage: stu.feedbackLanguage || "-",
      isWhatsApp: stu.receiveWhatsAppMessages ? "Yes" : "No",
      isEmail: stu.receiveEmailMessages ? "Yes" : "No",
      isLocked: stu.locked ? "Yes" : "No",
      status: stu.isActive === false ? "Inactive" : "Active",
      statusReason: reasonDisplay,
      edit: (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "flex-end" }}>
          {isLanguageInst && (
            isReportReady ? (
              <button
                className="table-assign-btn ready"
                title="Teacher evaluation report submitted - Click to review scorecard, teacher remarks & assign official CEFR level"
                onClick={() => handleOpenPlacementReview(stu, pt)}
              >
                <i className="fa-solid fa-clipboard-check"></i>
                <span>Review & Assign</span>
              </button>
            ) : !isAlreadyAssigned && pt ? (
              <button
                className="table-placement-status-btn"
                title="View placement test status and report"
                onClick={() => handleOpenPlacementReview(stu, pt)}
              >
                <i className="fa-solid fa-eye"></i>
                <span>Test Status</span>
              </button>
            ) : !isAlreadyAssigned && isLevelTestRequired ? (
              <button
                className="table-assign-btn"
                title="Level test required - Click to view details or assign level directly"
                onClick={() => handleOpenPlacementReview(stu, pt)}
              >
                <i className="fa-solid fa-user-tag"></i>
                <span>Assign Level</span>
              </button>
            ) : null
          )}
          <button
            className="edit-btn"
            onClick={() => {
              const cleanedPhone = cleanPhone(
                stu.phoneNumber || stu.formattedPhoneNumber || "",
              );
              const isDropout =
                stu.isActive === false &&
                stu.inActiveReason &&
                stu.inActiveReason.startsWith("Dropout_");
              const editState = {
                id: stu._id,
                firstName: stu.firstName,
                lastName: stu.lastName,
                referenceId: stu.referenceId,
                rollNo: stu.rollNo || "",
                email: stu.email,
                phoneNumber: cleanedPhone,
                receiveWhatsAppMessages: stu.receiveWhatsAppMessages,
                receiveEmailMessages: stu.receiveEmailMessages,
                locked: stu.locked || false,
                batch: stu.batch || null,
                level: stu.level || null,
                feedbackLanguage: stu.feedbackLanguage || null,
                isActive: stu.isActive !== false,
                inActiveReason: stu.inActiveReason || "",
                markedAsDropout: isDropout,
                dropoutAcademicYear: new Date().getFullYear(),
                password: "",
              };
              setEditStudent(editState);
              setInitialEditStudent({
                ...editState,
              });
              setIsEditModalOpen(true);
              setIsEditChanged(false);
            }}
            disabled={isUpdating}
          >
            <i className="fa-solid fa-pen-to-square"></i>
          </button>
        </div>
      ),
    };
  });

  const studentActions = (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "center", justifyContent: "flex-end" }}>
      {/* Active Students Toggle */}
      {students.some((s) => s.isActive === false) && (
        <label className="filter-toggle" style={{ margin: 0 }}>
          <span className="toggle-label">
            <input
              type="checkbox"
              checked={showActiveOnly}
              onChange={() => setShowActiveOnly((prev) => !prev)}
            />
            <span className="toggle-track">
              <span className="toggle-indicator"></span>
            </span>
            {showActiveOnly ? "Showing Active Students Only" : "Showing All Students"}
          </span>
        </label>
      )}

      {/* Send Message Button */}
      <button
        onClick={() => setIsChannelSelectionModalOpen(true)}
        className="btn-export"
        style={{
          background: "#3b82f6",
          color: "white",
          border: "none",
          margin: 0,
          opacity: selectedStudentIds.size === 0 ? 0.6 : 1,
          cursor: selectedStudentIds.size === 0 ? "not-allowed" : "pointer",
        }}
        disabled={selectedStudentIds.size === 0}
        title={
          selectedStudentIds.size === 0
            ? "No students selected"
            : "Send message to selected students"
        }
      >
        <i className="fa-solid fa-paper-plane"></i>
        &nbsp; Send Message {selectedStudentIds.size > 0 && `(${selectedStudentIds.size})`}
      </button>

      <button
        onClick={handleBulkDelete}
        className="btn-export"
        style={{
          background: "#ef4444",
          color: "white",
          border: "none",
          margin: 0,
          opacity: isBulkDeleting || selectedStudentIds.size === 0 ? 0.6 : 1,
          cursor: isBulkDeleting || selectedStudentIds.size === 0 ? "not-allowed" : "pointer",
        }}
        disabled={isBulkDeleting || selectedStudentIds.size === 0}
        title={
          selectedStudentIds.size === 0
            ? "No students selected"
            : "Remove selected students"
        }
      >
        {isBulkDeleting ? (
          <i className="fa-solid fa-spinner fa-spin"></i>
        ) : (
          <i className="fa-solid fa-trash"></i>
        )}
        &nbsp; Remove {selectedStudentIds.size > 0 && `(${selectedStudentIds.size})`}
      </button>

      {instituteCategory !== "HIGHER_EDUCATION" && hasLevelData && students.length > 0 && (
        <button
          onClick={() => setIsPromoteModalOpen(true)}
          className="btn-export"
          style={{ margin: 0, background: "#10b981", color: "white", border: "none" }}
          title="Promote students from one grade to another"
        >
          <i className="fa-solid fa-users-gear"></i>
          &nbsp; Promote Students
        </button>
      )}

      {students.length > 0 && (
        <button
          onClick={exportToExcel}
          className="btn-export"
          style={{ margin: 0 }}
          disabled={students.length === 0}
          title={
            students.length === 0
              ? "No students to export"
              : "Export students list to Excel"
          }
        >
          <FontAwesomeIcon icon={faDownload} /> Export
        </button>
      )}

      {students.length > 0 && (
        <button
          className="create-btn"
          style={{ margin: 0 }}
          onClick={() => setIsBulkUpdateModalOpen(true)}
          title="Perform bulk update on students"
        >
          <i className="fa-solid fa-file-pen"></i>
          &nbsp; Bulk Update
        </button>
      )}

      <button
        className="create-btn"
        style={{ margin: 0 }}
        onClick={() => setIsAddStudentsModalOpen(true)}
      >
        Add Students +
      </button>
    </div>
  );
  const readyReportCount = useMemo(() => {
    if (!isLanguageInst) return 0;
    return students.filter((s) => {
      const isAlreadyAssigned =
        Boolean(s.level && s.level !== "-") &&
        s.isActive !== false &&
        s.inActiveReason !== "Level Test Required";

      if (isAlreadyAssigned) return false;

      const pt = placementMap[s._id];
      if (!pt) return false;
      if (pt.placementStatus === "Approved") return false;

      return Boolean(
        pt.isReportReady ||
        (pt.writtenScore != null && pt.oralStatus === "Completed") ||
        pt.status === "ready_decision"
      );
    }).length;
  }, [isLanguageInst, students, placementMap]);

  return (
    <>
      <div className="manage-container">
        {loading && <Loader style={{ fontSize: "3rem" }} />}
        {errors.fetch && <div className="error-message">{errors.fetch}</div>}
        {!loading && !errors.fetch && (
          <>
            {isLanguageInst && readyReportCount > 0 && (
              <div
                style={{
                  backgroundColor: "#ecfdf5",
                  border: "1.5px solid #a7f3d0",
                  borderRadius: "10px",
                  padding: "0.85rem 1.25rem",
                  marginBottom: "1rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "10px",
                  boxShadow: "0 1px 3px rgba(16, 185, 129, 0.1)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      backgroundColor: "#10b981",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1rem",
                      flexShrink: 0,
                    }}
                  >
                    <i className="fa-solid fa-clipboard-check"></i>
                  </div>
                  <div>
                    <strong style={{ color: "#065f46", fontSize: "0.95rem" }}>
                      {readyReportCount} Teacher Evaluation Report{readyReportCount > 1 ? "s" : ""} Ready for Admin Review & Level Assignment
                    </strong>
                    <div style={{ color: "#047857", fontSize: "0.82rem", marginTop: "2px" }}>
                      Instructors have submitted oral viva scores & written exam marks. Click <strong>"Review & Assign Level"</strong> on any student row below to review complete teacher remarks, scorecard breakdowns, and confirm the official CEFR level.
                    </div>
                  </div>
                </div>
              </div>
            )}
            <Table
              ref={tableRef}
              data={enhancedStudents}
              columns={columns}
              caption="Students"
              onDataFiltered={handleDataFiltered}
              isCheckBoxAvailable={true}
              selectedRowIds={selectedStudentIds}
              onRowSelect={handleRowSelect}
              onSelectAll={handleSelectAll}
              disableRowClickSelection={true}
              searchActions={studentActions}
            />
          </>
        )}
      </div>

      {/* Add Students Modal - Excel-like data entry */}
      <AddStudentsModal
        isOpen={isAddStudentsModalOpen}
        onClose={() => setIsAddStudentsModalOpen(false)}
        onStudentsCreated={fetchStudents}
        instituteCategory={instituteCategory}
        levelOptions={levelOptions}
        batchYearOptions={batchYearOptions}
        autoGenerateStudentPassword={autoGenerateStudentPassword}
        isLanguageInst={isLanguageInst}
      />

      {/* Bulk Update Students Modal */}
      <BulkUpdateStudentsModal
        isOpen={isBulkUpdateModalOpen}
        onClose={() => setIsBulkUpdateModalOpen(false)}
        onStudentsUpdated={() => {
          fetchStudents();
          setSelectedStudentIds(new Set());
        }}
        students={students}
        feedbackLanguageOptions={feedbackLanguageOptions}
        selectedStudentIds={selectedStudentIds}
      />

      {/* Bulk Promote Modal */}
      <BulkPromoteModal
        isOpen={isPromoteModalOpen}
        onClose={() => {
          setIsPromoteModalOpen(false);
        }}
        students={students}
        levelOptions={levelOptions}
        onPromotionComplete={fetchStudents}
      />

      {/* Edit Student Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          if (!isUpdating) {
            setIsEditModalOpen(false);
            setErrors({});
            setShowEditPassword(false);
          }
        }}
      >
        <h2>Edit Student</h2>
        <p className="modal-description" style={{ marginBottom: "1rem" }}>
          Update the details below to edit the student information.
        </p>
        <form onSubmit={handleEditStudent} className="student-form">
          <div className="form-row">
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={editStudent.firstName}
                placeholder="Enter first name"
                onChange={handleEditInputChange}
                required
                disabled={isUpdating}
              />
              {errors.firstName && (
                <span className="error">{errors.firstName}</span>
              )}
            </div>
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={editStudent.lastName}
                placeholder="Enter last name"
                onChange={handleEditInputChange}
                required
                disabled={isUpdating}
              />
              {errors.lastName && (
                <span className="error">{errors.lastName}</span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Reference ID</label>
              <input
                type="text"
                name="referenceId"
                value={editStudent.referenceId}
                placeholder="Enter reference ID"
                onChange={handleEditInputChange}
                disabled={isUpdating}
              />
            </div>
            <div className="form-group">
              <label>Roll No</label>
              <input
                type="text"
                name="rollNo"
                value={editStudent.rollNo || ""}
                placeholder="Enter roll no"
                onChange={handleEditInputChange}
                disabled={isUpdating}
              />
            </div>
          </div>
          {errors.referenceId && (
            <span className="error">{errors.referenceId}</span>
          )}

          {/* Level - for non-HIGHER_EDUCATION */}
          {instituteCategory !== "HIGHER_EDUCATION" && (
            <div className="form-group">
              <label>Level</label>
              <Select
                value={
                  editStudent.level
                    ? {
                      value: editStudent.level,
                      label: editStudent.level,
                    }
                    : null
                }
                onChange={(selected) => {
                  setEditStudent((prev) => {
                    const updated = {
                      ...prev,
                      level: selected?.value || null,
                    };
                    setIsEditChanged(checkEditChanged(updated));
                    return updated;
                  });
                }}
                options={levelOptions}
                placeholder="Select Level"
                isDisabled={isUpdating || editStudent.markedAsDropout}
                isClearable
                classNamePrefix="react-select"
              />
            </div>
          )}

          {/* Mark as Dropped Out toggle - for non-HIGHER_EDUCATION */}
          {instituteCategory !== "HIGHER_EDUCATION" && (
            <div
              className="form-group"
              style={{
                border: editStudent.markedAsDropout
                  ? "1px solid #ef5350"
                  : "1px solid #e0e0e0",
                borderRadius: "10px",
                padding: "0.85rem 1rem",
                background: editStudent.markedAsDropout
                  ? "linear-gradient(135deg, #fce4ec, #fff3e0)"
                  : "#fafafa",
                transition: "all 0.3s ease",
              }}
            >
              <label
                style={{
                  fontWeight: 600,
                  color: editStudent.markedAsDropout ? "#c62828" : "inherit",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                }}
              >
                <i
                  className={`fa-solid fa-person-walking-arrow-right`}
                  style={{
                    color: "#ef5350",
                  }}
                ></i>
                Mark as Dropped Out
              </label>
              <label className="solution-toggle" style={{ marginTop: "0.4rem" }}>
                <input
                  type="checkbox"
                  checked={editStudent.markedAsDropout}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setEditStudent((prev) => {
                      const updated = {
                        ...prev,
                        markedAsDropout: checked,
                        // Clear level when marking as dropout
                        level: checked ? null : prev.level,
                      };
                      setIsEditChanged(checkEditChanged(updated));
                      return updated;
                    });
                  }}
                  disabled={isUpdating}
                />
                <span className="toggle-track">
                  <span className="toggle-indicator"></span>
                </span>
                <span className="toggle-label-text">
                  Dropped Out
                </span>
              </label>

              {editStudent.markedAsDropout && (
                <div style={{ marginTop: "0.75rem" }}>
                  <label
                    style={{
                      fontSize: "0.82rem",
                      fontWeight: 500,
                      color: "#555",
                      display: "block",
                      marginBottom: "0.35rem",
                    }}
                  >
                    <i
                      className="fa-solid fa-calendar-alt"
                      style={{ marginRight: "4px" }}
                    ></i>
                    Academic Year of Dropout
                  </label>
                  <Select
                    value={dropoutAcademicYearOptions.find(
                      (o) => o.value === editStudent.dropoutAcademicYear,
                    )}
                    onChange={(opt) => {
                      setEditStudent((prev) => {
                        const updated = {
                          ...prev,
                          dropoutAcademicYear:
                            opt?.value || new Date().getFullYear(),
                        };
                        setIsEditChanged(checkEditChanged(updated));
                        return updated;
                      });
                    }}
                    options={dropoutAcademicYearOptions}
                    isDisabled={isUpdating}
                    classNamePrefix="react-select"
                    menuPlacement="auto"
                  />
                  <small
                    style={{
                      display: "block",
                      marginTop: "0.4rem",
                      color: "#888",
                      fontSize: "0.75rem",
                    }}
                  >
                    Student will be marked as{" "}
                    <strong style={{ color: "#c62828" }}>
                      Dropout_
                      {editStudent.dropoutAcademicYear}-
                      {String(editStudent.dropoutAcademicYear + 1).slice(-2)}
                    </strong>{" "}
                    and set to inactive.
                  </small>
                </div>
              )}
            </div>
          )}

          {/* Batch (Year) - for HIGHER_EDUCATION */}
          {instituteCategory === "HIGHER_EDUCATION" && (
            <div className="form-group">
              <label>Batch</label>
              <Select
                value={
                  editStudent.batch
                    ? {
                      value: editStudent.batch,
                      label: String(editStudent.batch),
                    }
                    : null
                }
                onChange={(selected) => {
                  setEditStudent((prev) => {
                    const updated = {
                      ...prev,
                      batch: selected?.value || null,
                    };
                    setIsEditChanged(checkEditChanged(updated));
                    return updated;
                  });
                }}
                options={batchYearOptions}
                placeholder="Select Batch Year"
                isDisabled={isUpdating}
                isClearable
                classNamePrefix="react-select"
              />
            </div>
          )}

          {/* Feedback Language */}
          {feedbackLanguageOptions.length > 0 && (
            <div className="form-group">
              <label>Feedback Language</label>
              <Select
                value={
                  editStudent.feedbackLanguage
                    ? {
                      value: editStudent.feedbackLanguage,
                      label: editStudent.feedbackLanguage,
                    }
                    : null
                }
                onChange={(selected) => {
                  setEditStudent((prev) => ({
                    ...prev,
                    feedbackLanguage: selected?.value || null,
                  }));
                  setIsEditChanged(
                    (selected?.value || null) !==
                    (initialEditStudent.feedbackLanguage || null),
                  );
                }}
                options={feedbackLanguageOptions}
                placeholder="Select Feedback Language"
                isDisabled={isUpdating}
                classNamePrefix="react-select"
              />
            </div>
          )}

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={editStudent.email}
              placeholder="Enter student's email"
              onChange={handleEditInputChange}
              disabled={isUpdating}
            />
            {errors.email && <span className="error">{errors.email}</span>}
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <PhoneInput
              placeholder="Enter Phone Number"
              country={userCountry}
              value={editStudent.phoneNumber}
              onChange={(value, data, event, formattedValue) => {
                const cleaned = cleanPhone(formattedValue || value || "");
                setEditStudent((prev) => {
                  const updated = {
                    ...prev,
                    phoneNumber: cleaned,
                    receiveWhatsAppMessages: cleaned
                      ? prev.receiveWhatsAppMessages
                      : false,
                  };
                  setIsEditChanged(checkEditChanged(updated));
                  return updated;
                });
              }}
              inputStyle={{
                height: "45px",
                fontSize: "16px",
                width: "100%",
                paddingLeft: "48px",
              }}
              buttonStyle={{
                height: "45px",
              }}
              containerStyle={{
                width: "100%",
              }}
              enableSearch={true}
              disableSearchIcon={true}
              specialLabel="Enter Phone Number"
              dropdownClass="phone-input-dropdown"
              dropdownStyle={{
                maxHeight: "200px",
                overflowX: "hidden",
              }}
              disabled={isUpdating}
            />
          </div>
          {/* Change Password Section */}
          <div
            className="form-group"
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "10px",
              padding: "0.85rem 1rem",
              background: "#fafafa",
              transition: "all 0.3s ease",
            }}
          >
            <label
              style={{
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
              }}
            >
              <i className="fa-solid fa-key" style={{ color: "#f59e0b" }}></i>
              Change Password
            </label>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", alignItems: "flex-start", flexDirection: "column" }}>
              <div style={{ position: "relative", width: "100%" }}>
                <input
                  type={showEditPassword ? "text" : "password"}
                  name="password"
                  value={editStudent.password}
                  onChange={handleEditInputChange}
                  placeholder="Enter new password (min. 8 chars)"
                  disabled={isUpdating}
                  autoComplete="new-password"
                  style={{ width: "100%", paddingRight: "40px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowEditPassword((prev) => !prev)}
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#888",
                    padding: "4px",
                    fontSize: "14px",
                  }}
                  title={showEditPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  <i className={`fa-solid ${showEditPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                </button>
              </div>
              {errors.password && (
                <small style={{ color: "#ef4444", fontSize: "0.75rem", marginTop: "0.25rem", display: "block" }}>
                  {errors.password}
                </small>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Email Notifications</label>
            <label className="solution-toggle">
              <input
                type="checkbox"
                name="receiveEmailMessages"
                checked={editStudent.receiveEmailMessages}
                onChange={handleEditInputChange}
                disabled={isUpdating || !editStudent.email}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editStudent.receiveEmailMessages ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>

          <div className="form-group">
            <label>WhatsApp Notifications</label>
            <label className="solution-toggle">
              <input
                type="checkbox"
                name="receiveWhatsAppMessages"
                checked={editStudent.receiveWhatsAppMessages}
                onChange={handleEditInputChange}
                disabled={isUpdating || !editStudent.phoneNumber}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editStudent.receiveWhatsAppMessages ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>

          <div className="form-group">
            <label>Account Locked</label>
            <label className="solution-toggle">
              <input
                type="checkbox"
                name="locked"
                checked={editStudent.locked}
                onChange={handleEditInputChange}
                disabled={isUpdating}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editStudent.locked ? "Locked" : "Active"}
              </span>
            </label>
            <small className="helper-text" style={{ color: "#666" }}>
              Toggle to {editStudent.locked ? "unblock" : "block"} the student's
              account.
            </small>
          </div>
          {isUpdating ? (
            <Loader />
          ) : (
            <button
              type="submit"
              className="btn-submit"
              disabled={!isEditChanged || isUpdating}
              onClick={(e) => {
                if (!isEditChanged && !isUpdating) {
                  e.preventDefault();
                  swalWarning("No changes detected.");
                }
              }}
            >
              Update Student
            </button>
          )}
        </form>
      </Modal>

      {/* Step 1: Message Channel Selection Modal */}
      <Modal
        isOpen={isChannelSelectionModalOpen}
        onClose={() => setIsChannelSelectionModalOpen(false)}
      >
        <div className="gts-header">
          <h2>
            <i className="fa-solid fa-satellite-dish"></i>
            Choose Message Channel
          </h2>
          <p className="modal-description">
            Select how you want to reach the <strong>{selectedStudentIds.size}</strong> selected student(s).
          </p>
        </div>
        <div className="gts-options">
          {/* Email Channel */}
          <button
            type="button"
            className="gts-option"
            onClick={() => handleSelectChannel(["email"])}
          >
            <div className="gts-option-icon">
              <i className="fa-solid fa-envelope"></i>
            </div>
            <div className="gts-option-content">
              <div className="gts-option-title">Email Notification</div>
              <div className="gts-option-desc">
                Send a detailed message directly to the student's email inbox.
              </div>
            </div>
            <i className="fa-solid fa-chevron-right gts-option-arrow"></i>
          </button>

          {/* Push Channel */}
          <button
            type="button"
            className="gts-option"
            onClick={() => handleSelectChannel(["push"])}
          >
            <div className="gts-option-icon">
              <i className="fa-solid fa-bell"></i>
            </div>
            <div className="gts-option-content">
              <div className="gts-option-title">Push Notification</div>
              <div className="gts-option-desc">
                Send an immediate push alert to the student's device.
              </div>
            </div>
            <i className="fa-solid fa-chevron-right gts-option-arrow"></i>
          </button>
        </div>
      </Modal>

      {/* Step 2: Compose Message Modal */}
      <Modal
        isOpen={isComposeMessageModalOpen}
        onClose={() => {
          if (!isSendingMessage) {
            setIsComposeMessageModalOpen(false);
            setMessageForm({ subject: "", htmlBody: "", pushBody: "", attachments: [] });
            setSelectedChannels([]);
          }
        }}
      >
        <h2>Compose Message</h2>
        <p className="modal-description" style={{ marginBottom: "1.5rem" }}>
          You are sending a message to <strong>{selectedStudentIds.size}</strong> selected student(s) via <strong>
            {selectedChannels.includes('email') && selectedChannels.includes('push') ? "Email & Push Notification" : 
             selectedChannels.includes('email') ? "Email" : "Push Notification"}
          </strong>.
        </p>

        <form onSubmit={handleSendMessage} className="student-form">
          {selectedChannels.includes("email") && (
            <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "1.25rem", marginBottom: "1.25rem", background: "#f8fafc" }}>
              <h4 style={{ marginTop: 0, marginBottom: "1rem", color: "#334155" }}>
                <i className="fa-solid fa-envelope" style={{ color: "#3b82f6", marginRight: "0.5rem" }}></i> 
                Email Content
              </h4>
              <div className="form-group">
                <label>Subject <i className="fa-solid fa-asterisk requiredIcon"></i></label>
                <input
                  type="text"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Enter email subject"
                  disabled={isSendingMessage}
                  required={selectedChannels.includes("email")}
                  style={{ width: "100%", padding: "0.75rem", borderRadius: "8px", border: "1px solid #cbd5e1" }}
                />
              </div>
              <div className="form-group">
                <label>Email Body <i className="fa-solid fa-asterisk requiredIcon"></i></label>
                <textarea
                  value={messageForm.htmlBody}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, htmlBody: e.target.value }))}
                  placeholder="Enter email message body"
                  disabled={isSendingMessage}
                  rows={4}
                  required={selectedChannels.includes("email")}
                  style={{ width: "100%", padding: "0.75rem", borderRadius: "8px", border: "1px solid #cbd5e1", resize: "vertical" }}
                />
              </div>
              <div className="form-group">
                <label>
                  Attachments{" "}
                  <span style={{ color: "#64748b" }}>(optional, max 3 files, 10 MB each)</span>
                </label>

                <input
                  type="file"
                  multiple
                  onChange={(e) => {
                    const newFiles = Array.from(e.target.files || []);
                    const MAX_FILES = 3;
                    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

                    if (newFiles.length === 0) return;

                    setMessageForm((prev) => {
                      // merge with existing, de-duping on name+size+lastModified
                      const existingKeys = new Set(
                        prev.attachments.map((f) => `${f.name}-${f.size}-${f.lastModified}`)
                      );
                      const merged = [
                        ...prev.attachments,
                        ...newFiles.filter(
                          (f) => !existingKeys.has(`${f.name}-${f.size}-${f.lastModified}`)
                        ),
                      ];

                      if (merged.length > MAX_FILES) {
                        swalError(`You can attach a maximum of ${MAX_FILES} files.`);
                        e.target.value = "";
                        return prev;
                      }

                      const oversizedFile = merged.find((file) => file.size > MAX_FILE_SIZE);
                      if (oversizedFile) {
                        swalError(`"${oversizedFile.name}" exceeds the 10 MB file size limit.`);
                        e.target.value = "";
                        return prev;
                      }

                      e.target.value = ""; // reset so re-picking the same file later still fires onChange
                      return { ...prev, attachments: merged };
                    });
                  }}
                  disabled={isSendingMessage}
                />

                {messageForm.attachments?.length > 0 && (
                  <div style={{ marginTop: "0.5rem" }}>
                    {messageForm.attachments.map((file, index) => (
                      <div
                        key={`${file.name}-${file.lastModified}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "0.5rem",
                          marginTop: "0.25rem",
                        }}
                      >
                        <small style={{ color: "#64748b" }}>
                          {index + 1}. {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                        </small>
                        <button
                          type="button"
                          onClick={() =>
                            setMessageForm((prev) => ({
                              ...prev,
                              attachments: prev.attachments.filter((_, i) => i !== index),
                            }))
                          }
                          disabled={isSendingMessage}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#ef4444",
                            cursor: isSendingMessage ? "not-allowed" : "pointer",
                            padding: "2px 6px",
                          }}
                          title="Remove attachment"
                        >
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedChannels.includes("push") && (
            <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "1.25rem", marginBottom: "1.25rem", background: "#f8fafc" }}>
              <h4 style={{ marginTop: 0, marginBottom: "1rem", color: "#334155" }}>
                <i className="fa-solid fa-bell" style={{ color: "#f59e0b", marginRight: "0.5rem" }}></i> 
                Push Notification Content
              </h4>
              <div className="form-group">
                <label>Notification Body <i className="fa-solid fa-asterisk requiredIcon"></i></label>
                <textarea
                  value={messageForm.pushBody}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, pushBody: e.target.value }))}
                  placeholder="Enter push notification message"
                  disabled={isSendingMessage}
                  rows={3}
                  required={selectedChannels.includes("push")}
                  style={{ width: "100%", padding: "0.75rem", borderRadius: "8px", border: "1px solid #cbd5e1", resize: "vertical" }}
                />
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
            <button
              type="button"
              className="btn-cancel"
              style={{ padding: "0.75rem 1.5rem", borderRadius: "8px", border: "1px solid #cbd5e1", background: "white", cursor: "pointer", flex: 1 }}
              onClick={() => {
                if (!isSendingMessage) {
                  setIsComposeMessageModalOpen(false);
                  setIsChannelSelectionModalOpen(true);
                }
              }}
              disabled={isSendingMessage}
            >
              <i className="fa-solid fa-arrow-left"></i> Back
            </button>
            <button
              type="submit"
              className="btn-submit"
              style={{ background: "#3b82f6", flex: 2, padding: "0.75rem 1.5rem" }}
              disabled={(!selectedChannels.includes("email") && !selectedChannels.includes("push")) || isSendingMessage}
            >
              {isSendingMessage ? (
                <><i className="fa-solid fa-spinner fa-spin"></i> Sending...</>
              ) : (
                <><i className="fa-solid fa-paper-plane"></i> Send Message</>
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Admin Level Test Review Modal */}
      <Modal
        isOpen={isPlacementReviewModalOpen}
        onClose={() => {
          if (!isConfirmingLevel) {
            setIsPlacementReviewModalOpen(false);
            setSelectedPlacementStudent(null);
            setPlacementDetails(null);
          }
        }}
      >
        <div style={{ maxWidth: "680px", margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "0.5rem" }}>
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "10px",
                backgroundColor: "#ecfdf5",
                color: "#059669",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.25rem",
                flexShrink: 0,
              }}
            >
              <i className="fa-solid fa-graduation-cap"></i>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: "1.35rem", color: "#0f172a" }}>
                Level Test Evaluation Review
              </h2>
              <p style={{ margin: "2px 0 0 0", color: "#64748b", fontSize: "0.85rem" }}>
                Review instructor's evaluation report and assign student's official CEFR level.
              </p>
            </div>
          </div>

          {/* Student Banner */}
          {selectedPlacementStudent && (
            <div
              style={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "0.75rem 1rem",
                marginTop: "1rem",
                marginBottom: "1rem",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <div>
                <strong style={{ fontSize: "0.95rem", color: "#1e293b" }}>
                  {selectedPlacementStudent.firstName} {selectedPlacementStudent.lastName}
                </strong>
                <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                  {selectedPlacementStudent.email} &bull; ID: {selectedPlacementStudent.referenceId || "N/A"}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span
                  style={{
                    backgroundColor: "#fef3c7",
                    color: "#92400e",
                    padding: "3px 8px",
                    borderRadius: "6px",
                    fontSize: "0.75rem",
                    fontWeight: "600",
                  }}
                >
                  Inactive &bull; Level Test
                </span>
              </div>
            </div>
          )}

          {isLoadingPlacementDetails ? (
            <div style={{ padding: "2.5rem 1rem", textAlign: "center" }}>
              <Loader style={{ fontSize: "2.5rem" }} />
              <p style={{ color: "#64748b", marginTop: "0.75rem" }}>
                Loading evaluation details & teacher notes...
              </p>
            </div>
          ) : (
            <>
              {/* Scorecard Summary */}
              {placementDetails ? (
                <div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                      gap: "0.75rem",
                      marginBottom: "1rem",
                    }}
                  >
                    {/* Written Exam */}
                    <div
                      style={{
                        backgroundColor: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        borderRadius: "8px",
                        padding: "0.75rem",
                        textAlign: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.72rem",
                          color: "#166534",
                          fontWeight: "700",
                          textTransform: "uppercase",
                        }}
                      >
                        Written Test
                      </span>
                      <div
                        style={{
                          fontSize: "1.35rem",
                          fontWeight: "700",
                          color: "#15803d",
                          marginTop: "2px",
                        }}
                      >
                        {placementDetails.scores?.written?.obtained ?? 0} /{" "}
                        {placementDetails.scores?.written?.max ?? 70}
                      </div>
                      <small style={{ color: "#166534", fontWeight: "600" }}>
                        {placementDetails.scores?.written?.percentage ?? 0}%
                      </small>
                    </div>

                    {/* Oral Viva */}
                    <div
                      style={{
                        backgroundColor: "#f0f9ff",
                        border: "1px solid #bae6fd",
                        borderRadius: "8px",
                        padding: "0.75rem",
                        textAlign: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.72rem",
                          color: "#0369a1",
                          fontWeight: "700",
                          textTransform: "uppercase",
                        }}
                      >
                        Oral Viva (Speaking)
                      </span>
                      <div
                        style={{
                          fontSize: "1.35rem",
                          fontWeight: "700",
                          color: "#0284c7",
                          marginTop: "2px",
                        }}
                      >
                        {placementDetails.scores?.oral?.obtained ?? 0} /{" "}
                        {placementDetails.scores?.oral?.max ?? 30}
                      </div>
                      <small style={{ color: "#0369a1", fontWeight: "600" }}>
                        Status: {placementDetails.scores?.oral?.status || "Completed"}
                      </small>
                    </div>

                    {/* Composite Score */}
                    <div
                      style={{
                        backgroundColor: "#faf5ff",
                        border: "1px solid #e9d5ff",
                        borderRadius: "8px",
                        padding: "0.75rem",
                        textAlign: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.72rem",
                          color: "#7e22ce",
                          fontWeight: "700",
                          textTransform: "uppercase",
                        }}
                      >
                        Composite Score
                      </span>
                      <div
                        style={{
                          fontSize: "1.35rem",
                          fontWeight: "700",
                          color: "#9333ea",
                          marginTop: "2px",
                        }}
                      >
                        {placementDetails.scores?.composite?.percentage ?? 0}%
                      </div>
                      <small style={{ color: "#7e22ce", fontWeight: "600" }}>
                        {placementDetails.scores?.composite?.totalObtained ?? 0} /{" "}
                        {placementDetails.scores?.composite?.totalMax ?? 100} pts
                      </small>
                    </div>
                  </div>

                  {/* Teacher Feedback & Messages to Admin */}
                  <div
                    style={{
                      backgroundColor: "#fffbeb",
                      border: "1px solid #fde68a",
                      borderRadius: "8px",
                      padding: "0.85rem 1rem",
                      marginBottom: "1.25rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "0.4rem",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: "700",
                          color: "#92400e",
                          fontSize: "0.88rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <i className="fa-solid fa-chalkboard-user"></i> Teacher Evaluation & Recommendations
                      </span>
                      {placementDetails.scores?.oral?.recommendation && (
                        <span
                          style={{
                            backgroundColor: "#fef3c7",
                            border: "1px solid #f59e0b",
                            color: "#b45309",
                            fontWeight: "700",
                            fontSize: "0.78rem",
                            padding: "2px 8px",
                            borderRadius: "6px",
                          }}
                        >
                          Teacher Suggests: {placementDetails.scores?.oral?.recommendation}
                        </span>
                      )}
                    </div>
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "0.86rem",
                        color: "#78350f",
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.5,
                      }}
                    >
                      {placementDetails.scores?.oral?.notes || (
                        <em>No specific teacher feedback notes recorded during oral viva.</em>
                      )}
                    </p>

                    {placementDetails.scores?.composite?.systemRecommendation && (
                      <div
                        style={{
                          marginTop: "6px",
                          fontSize: "0.8rem",
                          color: "#b45309",
                          borderTop: "1px dashed #fcd34d",
                          paddingTop: "6px",
                        }}
                      >
                        <strong>System CEFR Benchmark: </strong>
                        {placementDetails.scores?.composite?.systemRecommendation}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    border: "1px dashed #cbd5e1",
                    borderRadius: "8px",
                    padding: "1rem",
                    marginBottom: "1.25rem",
                    color: "#64748b",
                    fontSize: "0.86rem",
                    textAlign: "center",
                  }}
                >
                  <i
                    className="fa-solid fa-circle-info"
                    style={{ marginRight: "6px", color: "#3b82f6" }}
                  ></i>
                  {placementMap[selectedPlacementStudent?._id]
                    ? `Test assigned (Target: ${placementMap[selectedPlacementStudent?._id]?.targetLevel || "N/A"}), status: ${placementMap[selectedPlacementStudent?._id]?.status || "Pending"}. You can review progress or directly confirm their level below.`
                    : "No test has been completed yet for this candidate in the Level Test Zone. You can assign the level and activate the student now if ready."}
                </div>
              )}

              {/* Admin Level Assignment Form */}
              <form onSubmit={handleConfirmPlacementLevel}>
                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "1rem",
                    marginBottom: "1rem",
                  }}
                >
                  <h4
                    style={{
                      margin: "0 0 0.75rem 0",
                      color: "#1e293b",
                      fontSize: "0.95rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <i
                      className="fa-solid fa-user-check"
                      style={{ color: "#10b981" }}
                    ></i>
                    Level Assignment & Activation
                  </h4>

                  <div className="form-group" style={{ marginBottom: "1rem" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        color: "#334155",
                      }}
                    >
                      Official CEFR Level <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <Select
                      value={
                        levelOptions.find(
                          (opt) => opt.value === confirmLevelForm.level,
                        ) ||
                        (confirmLevelForm.level
                          ? {
                              value: confirmLevelForm.level,
                              label: confirmLevelForm.level,
                            }
                          : null)
                      }
                      onChange={(opt) =>
                        setConfirmLevelForm((prev) => ({
                          ...prev,
                          level: opt?.value || "",
                        }))
                      }
                      options={levelOptions}
                      placeholder="Select confirmed CEFR level (e.g. A1, B1.1)..."
                      isClearable
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: !confirmLevelForm.level
                            ? "#f59e0b"
                            : "#cbd5e1",
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: "1rem" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        color: "#334155",
                      }}
                    >
                      Admin Remarks / Admission Notes (Optional)
                    </label>
                    <textarea
                      rows={2}
                      value={confirmLevelForm.remarks}
                      onChange={(e) =>
                        setConfirmLevelForm((prev) => ({
                          ...prev,
                          remarks: e.target.value,
                        }))
                      }
                      placeholder="e.g. Approved based on teacher recommendation and evaluation score."
                      style={{
                        width: "100%",
                        padding: "0.5rem 0.75rem",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        fontSize: "0.85rem",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "0.85rem",
                        color: "#334155",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={confirmLevelForm.autoEnroll}
                        onChange={(e) =>
                          setConfirmLevelForm((prev) => ({
                            ...prev,
                            autoEnroll: e.target.checked,
                          }))
                        }
                      />
                      <span>
                        Auto-enroll student into active regular class for the selected level
                      </span>
                    </label>
                    <div
                      style={{
                        fontSize: "0.78rem",
                        color: "#15803d",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <i className="fa-solid fa-circle-check"></i>
                      Student will be marked as <strong>Active</strong> and admission notification sent.
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    marginTop: "1rem",
                  }}
                >
                  <button
                    type="button"
                    className="btn-cancel"
                    style={{
                      padding: "0.5rem 1rem",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      background: "white",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      if (!isConfirmingLevel) {
                        setIsPlacementReviewModalOpen(false);
                        setSelectedPlacementStudent(null);
                        setPlacementDetails(null);
                      }
                    }}
                    disabled={isConfirmingLevel}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-submit"
                    style={{
                      backgroundColor: "#10b981",
                      color: "white",
                      padding: "0.5rem 1.25rem",
                      borderRadius: "6px",
                      border: "none",
                      fontWeight: "600",
                      cursor:
                        isConfirmingLevel || !confirmLevelForm.level
                          ? "not-allowed"
                          : "pointer",
                      opacity:
                        isConfirmingLevel || !confirmLevelForm.level ? 0.7 : 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    disabled={isConfirmingLevel || !confirmLevelForm.level}
                  >
                    {isConfirmingLevel ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i> Confirming...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-check"></i> Confirm Level & Activate Student
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </Modal>
    </>
  );
});

export default ManageStudents;